<template>
  <ScrollIntersectionObserver>
    <slot name="subjectImage" />
    <HintText>{{ c('single_choice.select_one_answer') }}</HintText>
    <ChoiceFields
      data-test-id="ChoiceSingle_ChoiceFields"
      :fields="fields"
      :selected-field-ids="selectedFieldIds"
      :max-answers="1"
      @field-clicked="toggleAnswerChoice"
      @updated-text-choice="textInput => updateChoiceTextAnswer(textInput)"
    />
  </ScrollIntersectionObserver>
</template>
<script lang="ts">
import { type DeepReadonly, defineComponent, type PropType } from 'vue'

import { type Answer } from '../../../answer/models/Answer'
import { c } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'
import { type Field } from '../../../question/models/Field'
import ScrollIntersectionObserver from '../../../shared/components/dumb/ScrollIntersectionObserver.vue'

import ChoiceFields from './ChoiceFields.vue'

export default defineComponent({
  components: { ChoiceFields, HintText, ScrollIntersectionObserver },
  props: {
    fields: { required: true, type: Array as PropType<DeepReadonly<Array<Field>>> },
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
    selectedFieldIds: { required: true, type: Set<string> },
  },
  emits: {
    answersUpdated: (_newAnswers: Answer[]) => true,
  },
  setup(props, { emit }) {
    const toggleAnswerChoice = (fieldToggled: string): void => {
      emit(
        'answersUpdated',
        props.answers.map(answer => {
          if (answer.fieldId !== fieldToggled) {
            return unselectAnswer(answer)
          }
          if (answer.type !== 'choice')
            throw TypeError("Expected answer of type 'choice' or 'choiceText'")
          return {
            ...answer,
            value: {
              selected: !answer.value.selected,
            },
          }
        }),
      )
    }

    const updateChoiceTextAnswer = (textInput: string): void => {
      emit(
        'answersUpdated',
        props.answers.map(answer => {
          if (answer.fieldId !== 'other' || answer.type !== 'choiceText')
            return unselectAnswer(answer)
          return { ...answer, value: { selected: textInput.length > 0, textInput } }
        }),
      )
    }

    const unselectAnswer = (answer: Answer): Answer => {
      switch (answer.type) {
        case 'choice':
          return { ...answer, value: { selected: false } }
        case 'choiceText':
          return { ...answer, value: { ...answer.value, selected: false } }
        default:
          return answer
      }
    }

    return {
      toggleAnswerChoice,
      updateChoiceTextAnswer,
      c,
    }
  },
})
</script>
